// Survey UI
//
//  Copyright (c) 2025 MX8 Inc
//  All rights reserved.
//
//  This software is confidential and proprietary information of MX8.
//  You shall not disclose such Confidential Information and shall use it only
//  in accordance with the terms of the agreement you entered into with MX8.

import * as Sentry from "@sentry/react";
import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import { FingerprintProvider } from "@fingerprint/react";
import App from "./App";
import { ThemePreviewFrame } from "./themePreview/ThemePreviewFrame";

// Sentry initialization check
const shouldInitializeSentry = () => {
  const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
  const sentryENV = import.meta.env.VITE_SENTRY_ENVIRONMENT;
  // @ts-expect-error - window.Cypress is added by Cypress
  const isCypressTest = typeof window !== "undefined" && window.Cypress;

  return sentryDsn && sentryENV && !isCypressTest;
};

// Initialize Sentry with error handling
if (shouldInitializeSentry()) {
  try {
    Sentry.init({
      dsn: import.meta.env.VITE_SENTRY_DSN,
      integrations: [Sentry.browserProfilingIntegration()],
      debug: true,
      environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
      tracesSampleRate: 1.0,
      replaysOnErrorSampleRate: 0.1,
      replaysSessionSampleRate: 0.1,
      tracePropagationTargets: [
        /^http:\/\/localhost:3000\/.*/,
        /^https:\/\/u\.dev\.mx8\.io\/.*/,
        /^https:\/\/u\.stage\.mx8\.io\/.*/,
        /^https:\/\/u\.mx8\.io\/.*/,
      ],
    });
  } catch (error) {
    console.error("Failed to initialize Sentry:", error);
  }
}

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement,
);

const pathname = window.location.pathname.replace(/\/+$/, "");
const isThemePreview = pathname === "/theme-preview";

root.render(
  <React.StrictMode>
    {isThemePreview ? (
      <ThemePreviewFrame />
    ) : (
      <FingerprintProvider
        apiKey={import.meta.env.VITE_FPJS_API_KEY as string}
        endpoints={["https://metrics.mx8.io"]}
        region="us"
      >
        <App />
      </FingerprintProvider>
    )}
  </React.StrictMode>,
);
